import { Popover } from '@headlessui/react';
import { useFilterContext } from './FilterV2Context';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { ReactNode } from 'react';
import {
    CheckboxFilterItem,
    DateFilterItem,
    FilterItem,
    NumericFilterItem,
    SelectFilterItem,
    TextFilterItem,
    ToggleFilterItem,
} from './filterV2.types';
import { getDateConditionLabel, getNumericConditionLabel, getTextConditionLabel } from './utils';
import { TooltipV2 } from '../tooltip/TooltipV2';

type FilterPillsProps = {
    children: ReactNode | ReactNode[];
};

function FilterPills({ children }: FilterPillsProps): JSX.Element {
    const ctx = useFilterContext();
    const filters = ctx.getAll().filter((f) => (f.type === 'toggle' ? f.active : f.values.length));

    return (
        <ul className="flex items-center gap-2 gap-y-1 flex-wrap" data-filter-pill-list>
            <AnimatePresence>
                {filters.map((filter) => {
                    return (
                        <Popover key={filter.id} as={motion.li} className="inline-block">
                            {({ open }): JSX.Element => {
                                return (
                                    <>
                                        <Popover.Button
                                            className={clsx(
                                                'rounded-md px-2 py-1 border w-full outline-none flex items-center hover:border-indigo-200 hover:bg-indigo-50',
                                                open ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-gray-50'
                                            )}
                                            onClick={(): void => {
                                                ctx.focusFilter(filter.id);
                                            }}
                                        >
                                            <PillDescription filter={filter} />
                                            <span
                                                className="text-gray-500 h-4 w-4 cursor-pointer ml-2"
                                                onClick={(e): void => {
                                                    const draft = ctx.clear(filter.id);
                                                    ctx.finalize(draft);
                                                    ctx.focusFilter();
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <XMarkIcon />
                                            </span>
                                        </Popover.Button>
                                        <AnimatePresence
                                            initial={false}
                                            onExitComplete={(): void => {
                                                ctx.finalize();
                                            }}
                                        >
                                            {open && (
                                                <Popover.Panel
                                                    className="bg-white shadow-md rounded-md min-w-[250px] outline-none absolute mt-2 z-50"
                                                    static
                                                    as={motion.div}
                                                >
                                                    <div className="relative w-full">
                                                        <div className="p-3 border-b">
                                                            <p className="text-sm text-gray-900 font-medium">Filters</p>
                                                        </div>
                                                        <div className="w-full max-h-[300px] overflow-scroll">{children}</div>
                                                    </div>
                                                </Popover.Panel>
                                            )}
                                        </AnimatePresence>
                                    </>
                                );
                            }}
                        </Popover>
                    );
                })}
            </AnimatePresence>
        </ul>
    );
}

type PillDescriptionProps = {
    filter: FilterItem;
};

const CUT_OFF = 2;

function PillDescription({ filter }: PillDescriptionProps): JSX.Element {
    switch (filter.type) {
        case 'checkbox': {
            return <CheckboxDescription filter={filter} />;
        }
        case 'text': {
            return <TextDescription filter={filter} />;
        }
        case 'numeric': {
            return <NumericDescription filter={filter} />;
        }
        case 'date': {
            return <DateDescription filter={filter} />;
        }
        case 'toggle': {
            return <ToggleDescription filter={filter} />;
        }
        case 'select': {
            return <SelectDescription filter={filter} />;
        }

        default:
            return <></>;
    }
}

function CheckboxDescription({ filter }: { filter: CheckboxFilterItem }): JSX.Element {
    const valueCount = filter.values.length;

    return (
        <div className="text-sm text-gray-900 whitespace-nowrap overflow-hidden flex items-center">
            <span className="mr-1">{filter.label} is</span>
            {valueCount <= CUT_OFF ? (
                filter.values.map((v, i) => {
                    const label = filter.options.find((o) => o.value === v)?.label;
                    return (
                        <span key={i + v}>
                            <strong className="capitalize">{label}</strong>
                            {valueCount !== 1 && i !== valueCount - 1 && <span className="mx-1">or</span>}
                        </span>
                    );
                })
            ) : (
                <span className="flex items-center">
                    <strong>{filter.options.find((o) => o.value === filter.values[0])?.label}</strong>
                    <span className="block ml-1">
                        <TooltipV2
                            delayMs={750}
                            message={[...filter.values].slice(1).map((v) => filter.options.find((o) => o.value === v)?.label)}
                        >
                            <span>
                                + {valueCount - 1} {valueCount - 1 === 1 ? 'other' : 'others'}
                            </span>
                        </TooltipV2>
                    </span>
                </span>
            )}
        </div>
    );
}

function TextDescription({ filter }: { filter: TextFilterItem }): JSX.Element {
    const valueCount = filter.values.length;
    return (
        <div className="text-sm text-gray-900 whitespace-nowrap overflow-hidden flex items-center">
            <span className="mr-1">{filter.label}</span>
            <strong className="mr-1">{getTextConditionLabel(filter.condition)}</strong>
            {valueCount <= CUT_OFF ? (
                filter.values.map((v, i) => {
                    return (
                        <span key={i + v}>
                            <strong>{v}</strong>
                            {valueCount !== 1 && i !== valueCount - 1 && <span className="mx-1">or</span>}
                        </span>
                    );
                })
            ) : (
                <span className="flex items-center">
                    <strong>{filter.values[0]}</strong>
                    <span className="block ml-1">
                        <TooltipV2 delayMs={750} message={[...filter.values].slice(1).map((v) => v)}>
                            <span>
                                + {valueCount - 1} {valueCount - 1 === 1 ? 'other' : 'others'}
                            </span>
                        </TooltipV2>
                    </span>
                </span>
            )}
        </div>
    );
}

function NumericDescription({ filter }: { filter: NumericFilterItem }): JSX.Element {
    const valueCount = filter.values.length;
    return (
        <div className="text-sm text-gray-900 whitespace-nowrap overflow-hidden flex items-center">
            <span className="mr-1">{filter.label} is </span>

            {filter.condition === 'btw' ? (
                <>
                    <strong className="mr-1">{getNumericConditionLabel('gte')}</strong>
                    <strong>
                        {new Intl.NumberFormat().format(filter.values[0])}
                        {filter.isPercentage && '%'}
                    </strong>
                    <span className="mx-1">and</span>
                    <strong className="mr-1">{getNumericConditionLabel('lte')}</strong>
                    <strong>
                        {new Intl.NumberFormat().format(filter.values[1])}
                        {filter.isPercentage && '%'}
                    </strong>
                </>
            ) : (
                <>
                    <strong className="mr-1">{getNumericConditionLabel(filter.condition)}</strong>
                    {filter.values.map((v, i) => {
                        return (
                            <span key={i + v}>
                                <strong>
                                    {new Intl.NumberFormat().format(v)}
                                    {filter.isPercentage && '%'}
                                </strong>
                                {valueCount !== 1 && i !== valueCount - 1 && <span className="mx-1">or</span>}
                            </span>
                        );
                    })}
                </>
            )}
        </div>
    );
}

function DateDescription({ filter }: { filter: DateFilterItem }): JSX.Element {
    const valueCount = filter.values.length;
    return (
        <div className="text-sm text-gray-900 whitespace-nowrap overflow-hidden flex items-center">
            <span className="mr-1">{filter.label} is </span>

            {filter.condition === 'btw' ? (
                <>
                    <strong className="mr-1">{getDateConditionLabel('gt')}</strong>
                    <strong>{new Date(filter.values[0]).toLocaleDateString()}</strong>
                    <span className="mx-1">and</span>
                    <strong className="mr-1">{getDateConditionLabel('lt')}</strong>
                    <strong>{new Date(filter.values[1]).toLocaleDateString()}</strong>
                </>
            ) : (
                <>
                    <strong className="mr-1">{getDateConditionLabel(filter.condition)}</strong>

                    {filter.values.map((v, i) => {
                        return (
                            <span key={i + v}>
                                <strong>{new Date(filter.values[0]).toLocaleDateString()}</strong>
                                {valueCount !== 1 && i !== valueCount - 1 && <span className="mx-1">or</span>}
                            </span>
                        );
                    })}
                </>
            )}
        </div>
    );
}

function ToggleDescription({ filter }: { filter: ToggleFilterItem }): JSX.Element {
    return (
        <div className="text-sm text-gray-900 whitespace-nowrap overflow-hidden flex items-center">
            <span>
                <strong className="mr-1">{filter.prefix}</strong>
                {filter.label}
            </span>
        </div>
    );
}

function SelectDescription({ filter }: { filter: SelectFilterItem }): JSX.Element {
    const valueCount = filter.values.length;

    return (
        <div className="text-sm text-gray-900 whitespace-nowrap overflow-hidden flex items-center">
            <span className="mr-1">{filter.label} is</span>
            {valueCount <= CUT_OFF ? (
                filter.values.map((v, i) => {
                    const label = filter.values.find((o) => o.value === v.value)?.label;
                    return (
                        <span key={i + v.value.toString()}>
                            <strong className="capitalize">{label}</strong>
                            {valueCount !== 1 && i !== valueCount - 1 && <span className="mx-1">or</span>}
                        </span>
                    );
                })
            ) : (
                <span className="flex items-center">
                    <strong>{filter.values.find((o) => o.value === filter.values[0].value)?.label}</strong>
                    <span className="block ml-1">
                        <TooltipV2
                            delayMs={750}
                            message={[...filter.values].slice(1).map((v) => filter.values.find((o) => o.value === v.value)?.label)}
                        >
                            <span>
                                + {valueCount - 1} {valueCount - 1 === 1 ? 'other' : 'others'}
                            </span>
                        </TooltipV2>
                    </span>
                </span>
            )}
        </div>
    );
}

export default FilterPills;
